import { useCallback } from 'react';

import type {
  Cut,
  CutsheetCatalog,
  Extra,
  Primal,
  Specification,
  Trim,
} from '../types';

import {
  useCutsheetCapabilityCutDeleteMutation,
  useCutsheetCapabilityCutManyQuery,
  useCutsheetCapabilityCutUpsertMutation,
  useCutsheetCapabilityExtraDeleteMutation,
  useCutsheetCapabilityExtraManyQuery,
  useCutsheetCapabilityExtraUpsertMutation,
  useCutsheetCapabilityGroupDeleteMutation,
  useCutsheetCapabilityGroupManyQuery,
  useCutsheetCapabilityGroupReorderMutation,
  useCutsheetCapabilityGroupUpsertMutation,
  useCutsheetCapabilityOptionReorderMutation,
  useCutsheetCapabilitySpecificationDeleteMutation,
  useCutsheetCapabilitySpecificationManyQuery,
  useCutsheetCapabilitySpecificationReorderMutation,
  useCutsheetCapabilitySpecificationUpsertMutation,
  useCutsheetCapabilityTrimDeleteMutation,
  useCutsheetCapabilityTrimManyQuery,
  useCutsheetCapabilityTrimUpsertMutation,
} from '../../../../lib/graphql';
import { removeTypename } from '@farmshare/utils';

export interface UseCatalogStateOptions {
  vendorId: string;
  capabilityId: string;
  /** Whether to fetch only active items */
  isActive?: boolean;
}

/**
 * Hook for managing cutsheet catalog state with GraphQL.
 * Provides CRUD operations for all catalog entities:
 * primals, cuts, trim options, extras, and specifications.
 * @param options - Configuration for fetching catalog items
 * @param options.vendorId - The vendor ID (used for catalog metadata).
 * @param options.capabilityId - The capability ID to fetch catalog items for.
 * @param options.isActive - Whether to filter to active items only.
 * @returns Catalog data and operations
 */
export const useCatalogState = ({
  vendorId,
  capabilityId,
  isActive = true,
}: UseCatalogStateOptions) => {
  // Fetch all catalog items
  const {
    data: primalsData,
    loading: primalsLoading,
    error: primalsError,
    refetch: refetchPrimals,
  } = useCutsheetCapabilityGroupManyQuery({
    variables: { capabilityId, isActive },
    skip: !capabilityId,
  });

  const {
    data: cutsData,
    loading: cutsLoading,
    error: cutsError,
    refetch: refetchCuts,
  } = useCutsheetCapabilityCutManyQuery({
    variables: { capabilityId, isActive },
    skip: !capabilityId,
  });

  const {
    data: trimData,
    loading: trimLoading,
    error: trimError,
    refetch: refetchTrim,
  } = useCutsheetCapabilityTrimManyQuery({
    variables: { capabilityId, isActive },
    skip: !capabilityId,
  });

  const {
    data: extrasData,
    loading: extrasLoading,
    error: extrasError,
    refetch: refetchExtras,
  } = useCutsheetCapabilityExtraManyQuery({
    variables: { capabilityId, isActive },
    skip: !capabilityId,
  });

  const {
    data: specificationsData,
    loading: specificationsLoading,
    error: specificationsError,
    refetch: refetchSpecifications,
  } = useCutsheetCapabilitySpecificationManyQuery({
    variables: { capabilityId, isActive },
    skip: !capabilityId,
  });

  // Primal mutations
  const [upsertPrimalMutation] = useCutsheetCapabilityGroupUpsertMutation();
  const [deletePrimalMutation] = useCutsheetCapabilityGroupDeleteMutation();

  // Cut mutations
  const [upsertCutMutation] = useCutsheetCapabilityCutUpsertMutation();
  const [deleteCutMutation] = useCutsheetCapabilityCutDeleteMutation();

  // Trim mutations
  const [upsertTrimMutation] = useCutsheetCapabilityTrimUpsertMutation();
  const [deleteTrimMutation] = useCutsheetCapabilityTrimDeleteMutation();

  // Extra mutations
  const [upsertExtraMutation] = useCutsheetCapabilityExtraUpsertMutation();
  const [deleteExtraMutation] = useCutsheetCapabilityExtraDeleteMutation();

  // Specification mutations
  const [upsertSpecificationMutation] =
    useCutsheetCapabilitySpecificationUpsertMutation();
  const [deleteSpecificationMutation] =
    useCutsheetCapabilitySpecificationDeleteMutation();

  // Bulk reorder mutations — single DB round-trip via bulkWrite.
  const [primalReorderMutation] = useCutsheetCapabilityGroupReorderMutation();
  const [optionReorderMutation] = useCutsheetCapabilityOptionReorderMutation();
  const [specReorderMutation] =
    useCutsheetCapabilitySpecificationReorderMutation();

  // Transform GraphQL data to match frontend types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformPrimal = (item: any): Primal => ({
    id: item._id,
    type: 'primal',
    capabilityId: item.capabilityId,
    title: item.title,
    sortOrder: item.sortOrder ?? 0,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformCut = (item: any): Cut => ({
    id: item._id,
    type: 'cut',
    capabilityId: item.capabilityId,
    title: item.title,
    description: item.description,
    imageUrl: item.imageUrl,
    printedText: item.printedText,
    groupId: item.groupId,
    sortOrder: item.sortOrder ?? 0,
    specifications: item.specifications,
    pricing: item.pricing,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformTrim = (item: any): Trim => ({
    id: item._id,
    type: 'trim',
    capabilityId: item.capabilityId,
    title: item.title,
    description: item.description,
    imageUrl: item.imageUrl,
    printedText: item.printedText,
    isDefault: item.isDefault,
    minQty: item.minQty,
    sortOrder: item.sortOrder ?? 0,
    specifications: item.specifications,
    pricing: item.pricing,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformExtra = (item: any): Extra => ({
    id: item._id,
    type: 'extra',
    capabilityId: item.capabilityId,
    title: item.title,
    description: item.description,
    imageUrl: item.imageUrl,
    printedText: item.printedText,
    hasCharge: item.hasCharge,
    sortOrder: item.sortOrder ?? 0,
    specifications: item.specifications,
    pricing: item.pricing,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformSpecification = (item: any): Specification => ({
    id: item._id,
    type: 'specification',
    capabilityId: item.capabilityId,
    title: item.title,
    description: item.description,
    imageUrl: item.imageUrl,
    printedText: item.printedText,
    inputType: item.inputType,
    options: item.options,
    unit: item.unit,
    defaultValue: item.defaultValue,
    required: item.required,
    assignedToIds: item.assignedToIds,
    pricingAdjustments: item.pricingAdjustments,
    sortOrder: item.sortOrder ?? 0,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  });

  // Build catalog from GraphQL data
  const catalog: CutsheetCatalog = {
    id: '', // Set when capabilities is loaded
    vendorId,
    species: '', // Set when capability is loaded
    name: '', // Set when capabilities is loaded
    description: undefined,
    primals: [
      ...(primalsData?.cutsheetGroupsByVendorAndAnimal?.map(transformPrimal) ||
        []),
    ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    cuts: [
      ...(cutsData?.cutsheetCutsByVendorAndAnimal?.map(transformCut) || []),
    ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    trim: [
      ...(trimData?.cutsheetTrimByVendorAndAnimal?.map(transformTrim) || []),
    ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    extras: [
      ...(extrasData?.cutsheetExtrasByVendorAndAnimal?.map(transformExtra) ||
        []),
    ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    specifications: [
      ...(specificationsData?.cutsheetSpecificationsByVendorAndAnimal?.map(
        transformSpecification,
      ) || []),
    ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  // Primal operations
  const addPrimal = useCallback(
    async (
      primal: Omit<
        Primal,
        'id' | 'createdAt' | 'updatedAt' | 'type' | 'capabilityId'
      >,
    ) => {
      await upsertPrimalMutation({
        variables: {
          vendorId,
          capabilityId,
          title: primal.title,
          isActive: true,
        },
      });
      refetchPrimals();
    },
    [vendorId, capabilityId, upsertPrimalMutation, refetchPrimals],
  );

  const updatePrimal = useCallback(
    async (id: string, updates: Partial<Omit<Primal, 'capabilityId'>>) => {
      const existing = catalog.primals.find((p) => p.id === id);
      if (!existing) return;

      await upsertPrimalMutation({
        variables: {
          id,
          vendorId,
          capabilityId,
          title: updates.title ?? existing.title,
          isActive: true,
        },
      });
      refetchPrimals();
    },
    [
      catalog.primals,
      vendorId,
      capabilityId,
      upsertPrimalMutation,
      refetchPrimals,
    ],
  );

  const removePrimal = useCallback(
    async (id: string) => {
      await deletePrimalMutation({ variables: { id } });
      refetchPrimals();
    },
    [deletePrimalMutation, refetchPrimals],
  );

  // Cut operations
  const addCut = useCallback(
    async (
      cut: Omit<Cut, 'id' | 'createdAt' | 'updatedAt' | 'capabilityId'>,
    ) => {
      await upsertCutMutation({
        variables: removeTypename({
          vendorId,
          capabilityId,
          groupId: cut.groupId,
          title: cut.title,
          description: cut.description,
          imageUrl: cut.imageUrl,
          printedText: cut.printedText,
          specifications: cut.specifications,
          pricing: cut.pricing?.model ? cut.pricing : undefined,
          isActive: true,
        }),
      });
      refetchCuts();
    },
    [vendorId, capabilityId, upsertCutMutation, refetchCuts],
  );

  const updateCut = useCallback(
    async (id: string, updates: Partial<Omit<Cut, 'capabilityId'>>) => {
      const existing = catalog.cuts.find((c) => c.id === id);
      if (!existing) return;

      await upsertCutMutation({
        variables: removeTypename({
          id,
          vendorId,
          capabilityId,
          groupId: updates.groupId ?? existing.groupId,
          title: updates.title ?? existing.title,
          description: updates.description ?? existing.description,
          imageUrl: updates.imageUrl ?? existing.imageUrl,
          printedText: updates.printedText ?? existing.printedText,
          specifications: updates.specifications ?? existing.specifications,
          pricing: (updates.pricing ?? existing.pricing)?.model
            ? (updates.pricing ?? existing.pricing)
            : null,
          isActive: true,
        }),
      });
      refetchCuts();
    },
    [catalog.cuts, vendorId, capabilityId, upsertCutMutation, refetchCuts],
  );

  const removeCut = useCallback(
    async (id: string) => {
      await deleteCutMutation({ variables: { id } });
      refetchCuts();
    },
    [deleteCutMutation, refetchCuts],
  );

  // Trim operations
  const addTrim = useCallback(
    async (
      trim: Omit<Trim, 'id' | 'createdAt' | 'updatedAt' | 'capabilityId'>,
    ) => {
      await upsertTrimMutation({
        variables: removeTypename({
          vendorId,
          capabilityId,
          title: trim.title,
          description: trim.description,
          imageUrl: trim.imageUrl,
          isDefault: trim.isDefault,
          printedText: trim.printedText,
          specifications: trim.specifications,
          pricing: trim.pricing?.model ? trim.pricing : undefined,
          minQty: trim.minQty,
          isActive: true,
        }),
      });
      refetchTrim();
    },
    [vendorId, capabilityId, upsertTrimMutation, refetchTrim],
  );

  const updateTrim = useCallback(
    async (id: string, updates: Partial<Omit<Trim, 'capabilityId'>>) => {
      const existing = catalog.trim.find((t) => t.id === id);
      if (!existing) return;

      await upsertTrimMutation({
        variables: removeTypename({
          id,
          vendorId,
          capabilityId,
          title: updates.title ?? existing.title,
          description: updates.description ?? existing.description,
          imageUrl: updates.imageUrl ?? existing.imageUrl,
          isDefault: updates.isDefault ?? existing.isDefault,
          printedText: updates.printedText ?? existing.printedText,
          specifications: updates.specifications ?? existing.specifications,
          pricing: (updates.pricing ?? existing.pricing)?.model
            ? (updates.pricing ?? existing.pricing)
            : null,
          isActive: true,
          minQty: updates.minQty ?? existing.minQty,
        }),
      });
      refetchTrim();
    },
    [catalog.trim, vendorId, capabilityId, upsertTrimMutation, refetchTrim],
  );

  const removeTrim = useCallback(
    async (id: string) => {
      await deleteTrimMutation({ variables: { id } });
      refetchTrim();
    },
    [deleteTrimMutation, refetchTrim],
  );

  // Extra operations
  const addExtra = useCallback(
    async (
      extra: Omit<Extra, 'id' | 'createdAt' | 'updatedAt' | 'capabilityId'>,
    ) => {
      await upsertExtraMutation({
        variables: removeTypename({
          vendorId,
          capabilityId,
          title: extra.title,
          description: extra.description,
          imageUrl: extra.imageUrl,
          hasCharge: extra.hasCharge,
          printedText: extra.printedText,
          specifications: extra.specifications,
          pricing: extra.pricing?.model ? extra.pricing : undefined,
          isActive: true,
        }),
      });
      refetchExtras();
    },
    [vendorId, capabilityId, upsertExtraMutation, refetchExtras],
  );

  const updateExtra = useCallback(
    async (id: string, updates: Partial<Omit<Extra, 'capabilityId'>>) => {
      const existing = catalog.extras.find((e) => e.id === id);
      if (!existing) return;

      await upsertExtraMutation({
        variables: removeTypename({
          id,
          vendorId,
          capabilityId,
          title: updates.title ?? existing.title,
          description: updates.description ?? existing.description,
          imageUrl: updates.imageUrl ?? existing.imageUrl,
          hasCharge: updates.hasCharge ?? existing.hasCharge,
          printedText: updates.printedText ?? existing.printedText,
          specifications: updates.specifications ?? existing.specifications,
          pricing: (updates.pricing ?? existing.pricing)?.model
            ? (updates.pricing ?? existing.pricing)
            : null,
          isActive: true,
        }),
      });
      refetchExtras();
    },
    [
      catalog.extras,
      vendorId,
      capabilityId,
      upsertExtraMutation,
      refetchExtras,
    ],
  );

  const removeExtra = useCallback(
    async (id: string) => {
      await deleteExtraMutation({ variables: { id } });
      refetchExtras();
    },
    [deleteExtraMutation, refetchExtras],
  );

  // Specification operations
  const addSpecification = useCallback(
    async (
      spec: Omit<
        Specification,
        'id' | 'createdAt' | 'updatedAt' | 'capabilityId'
      >,
    ) => {
      await upsertSpecificationMutation({
        variables: {
          vendorId,
          capabilityId,
          title: spec.title,
          description: spec.description,
          imageUrl: spec.imageUrl,
          inputType: spec.inputType,
          printedText: spec.printedText,
          options: spec.options,
          unit: spec.unit,
          defaultValue: spec.defaultValue,
          required: spec.required,
          assignedToIds: spec.assignedToIds,
          pricingAdjustments: spec.pricingAdjustments,
          isActive: true,
        },
      });
      refetchSpecifications();
    },
    [
      vendorId,
      capabilityId,
      upsertSpecificationMutation,
      refetchSpecifications,
    ],
  );

  const updateSpecification = useCallback(
    async (
      id: string,
      updates: Partial<Omit<Specification, 'capabilityId'>>,
    ) => {
      const existing = catalog.specifications.find((s) => s.id === id);
      if (!existing) return;

      await upsertSpecificationMutation({
        variables: {
          id,
          vendorId,
          capabilityId,
          title: updates.title ?? existing.title,
          description: updates.description ?? existing.description,
          imageUrl: updates.imageUrl ?? existing.imageUrl,
          inputType: updates.inputType ?? existing.inputType,
          printedText: updates.printedText ?? existing.printedText,
          options: updates.options ?? existing.options,
          unit: updates.unit ?? existing.unit,
          defaultValue: updates.defaultValue ?? existing.defaultValue,
          required: updates.required ?? existing.required,
          assignedToIds: updates.assignedToIds ?? existing.assignedToIds,
          pricingAdjustments:
            updates.pricingAdjustments ?? existing.pricingAdjustments,
          isActive: true,
        },
      });
      refetchSpecifications();
    },
    [
      catalog.specifications,
      vendorId,
      capabilityId,
      upsertSpecificationMutation,
      refetchSpecifications,
    ],
  );

  const removeSpecification = useCallback(
    async (id: string) => {
      await deleteSpecificationMutation({ variables: { id } });
      refetchSpecifications();
    },
    [deleteSpecificationMutation, refetchSpecifications],
  );

  // ===== REORDER OPERATIONS =====
  // Each function sends all IDs in one bulk request to the API, which
  // issues a single MongoDB bulkWrite — far cheaper than N individual upserts.

  /**
   * Reorder primals by updating their sortOrder in one bulk operation.
   * @param orderedIds - Primal IDs in the desired display order.
   * @returns Promise that resolves when the reorder is persisted.
   */
  const reorderPrimals = useCallback(
    async (orderedIds: string[]) => {
      await primalReorderMutation({ variables: { ids: orderedIds } });
      refetchPrimals();
    },
    [primalReorderMutation, refetchPrimals],
  );

  /**
   * Reorder cuts by updating their sortOrder in one bulk operation.
   * @param orderedIds - Cut IDs in the desired display order.
   * @returns Promise that resolves when the reorder is persisted.
   */
  const reorderCuts = useCallback(
    async (orderedIds: string[]) => {
      await optionReorderMutation({ variables: { ids: orderedIds } });
      refetchCuts();
    },
    [optionReorderMutation, refetchCuts],
  );

  /**
   * Reorder trim options by updating their sortOrder in one bulk operation.
   * @param orderedIds - Trim IDs in the desired display order.
   * @returns Promise that resolves when the reorder is persisted.
   */
  const reorderTrim = useCallback(
    async (orderedIds: string[]) => {
      await optionReorderMutation({ variables: { ids: orderedIds } });
      refetchTrim();
    },
    [optionReorderMutation, refetchTrim],
  );

  /**
   * Reorder extras by updating their sortOrder in one bulk operation.
   * @param orderedIds - Extra IDs in the desired display order.
   * @returns Promise that resolves when the reorder is persisted.
   */
  const reorderExtras = useCallback(
    async (orderedIds: string[]) => {
      await optionReorderMutation({ variables: { ids: orderedIds } });
      refetchExtras();
    },
    [optionReorderMutation, refetchExtras],
  );

  /**
   * Reorder specifications by updating their sortOrder in one bulk operation.
   * @param orderedIds - Specification IDs in the desired display order.
   * @returns Promise that resolves when the reorder is persisted.
   */
  const reorderSpecifications = useCallback(
    async (orderedIds: string[]) => {
      await specReorderMutation({ variables: { ids: orderedIds } });
      refetchSpecifications();
    },
    [specReorderMutation, refetchSpecifications],
  );

  // Update catalog metadata (name, description, etc.)
  // Note: This would require a separate capabilities mutation
  const updateCatalogInfo = useCallback(
    (
      _updates: Partial<
        Pick<CutsheetCatalog, 'name' | 'description' | 'species'>
      >,
    ) => {
      // TODO: implement when a CutsheetCapabilities document mutation is available
      // updateCatalogInfo requires a separate capabilities-level mutation
    },
    [],
  );

  const loading =
    primalsLoading ||
    cutsLoading ||
    trimLoading ||
    extrasLoading ||
    specificationsLoading;

  const error =
    primalsError ||
    cutsError ||
    trimError ||
    extrasError ||
    specificationsError;

  return {
    catalog,
    loading,
    error,
    // Primal operations
    addPrimal,
    updatePrimal,
    removePrimal,
    reorderPrimals,
    // Cut operations
    addCut,
    updateCut,
    removeCut,
    reorderCuts,
    // Trim operations
    addTrim,
    updateTrim,
    removeTrim,
    reorderTrim,
    // Extra operations
    addExtra,
    updateExtra,
    removeExtra,
    reorderExtras,
    // Specification operations
    addSpecification,
    updateSpecification,
    removeSpecification,
    reorderSpecifications,
    // Catalog metadata
    updateCatalogInfo,
    // Refetch functions
    refetch: () => {
      refetchPrimals();
      refetchCuts();
      refetchTrim();
      refetchExtras();
      refetchSpecifications();
    },
  };
};
